import logoWhite from '@assets/images/mandrake-logo-large-white.svg'
import useModalContext from "@contexts/modalContext"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import BaseModal from "@elements/BaseModal"
import styled from '@emotion/styled'
import { colors, fontSize, size } from '@styles/theme'
import { FormTextField, SheerFormTextField } from '@elements/Inputs'
import { Alert, Button, ButtonBase, LinearProgress, Popover } from '@mui/material'
import { PiArrowRightBold, PiInfo } from 'react-icons/pi'
import { useState } from 'react'
import { EyeMaskEndAdornment } from '@elements/InputAdornments'
import { PositionedTopProgress } from '@elements/Loaders'
import useTrigger from '@hooks/useTrigger'
import snakecaseKeys from 'snakecase-keys'
import axios from '@utils/webRequests'
import { ActionButton, BottomButtonGroup, ButtonGroupClearFix, FlexSpacerDiv, FormContainer, FormLink, FormTitle, InputContainer, LinksContainer, RightLabelText } from './landing/LandingElements'

// TODO: Put into elements for modular use, refactor, and replace in PasswordReset
const StyledAlert = ({ kind, children, AlertStyles }) => {
  const styles = {
    success: {
      backgroundColor: '#0C130D',
      color: '#CCE8CD',
    },
    info: {
      backgroundColor: '#071318',
      color: '#B8E7FB'
    },
    warning: {
      backgroundColor: '#191207',
      color: '#FFE2B7'
    },
    error: {
      backgroundColor: '#160B0B',
      color: '#F4C7C7'
    },
  }

  return (
    <Alert
      // variant="outlined"
      severity={kind}
      // sx={{ width: '100%' }}
      style={{
        width: '100%',
        height: size(8),
        borderRadius: 10,
        fontSize: fontSize('md'),
        marginTop: size(2),
        display: 'flex',
        alignItems: 'center',
        'MuiAlert-action': { padding: 0 },
        ...(styles[kind] || styles['success']),
        ...AlertStyles
      }}
    >
      {children}
    </Alert>
  )
}

// TODO: Put into auth components and refactor
const PasswordRequirementsAlert = () => (
  <StyledAlert kind='info' AlertStyles={{ height: 'unset' }}>
    Password must have:
    <ul style={{ margin: '0', padding: 0, listStyle: 'inside' }}>
      <li>{'> 5 characters'}</li>
      <li>One lowercase</li>
      <li>One uppercase</li>
      <li>
        One symbol
        <br />
        {"~`!@#$%^&*\(\)\-_+=\{\}\[\]|\;:\"'<>,.\/?"}
      </li>
    </ul>
  </StyledAlert>
)

const SignupModalEmailPass = ({ formState, formErrors, setPasswordErrors, handleChange, actionHandler, isLoading, isLoadingDelayed }) => {
  const [isPasswordMasked, setIsPasswordMasked] = useState(true)
  const [isPasswordValid, setIsPasswordValid] = useState(false)
  const [searchParams] = useSearchParams()
  const [anchorEl, setAnchorEl] = useState(null)
  const isBlockingAccess = searchParams.get('blocked_access') || false
  const isRegisteringBeforeSubscribe = searchParams.get('register_before_subscribe') || false

  useTrigger(formState?.password, 500, () => {
    if (formState.password) {
      validateUserPassword()
    } else {
      setIsPasswordValid(false)
    }
  })

  const validateUserPassword = () => {
    axios.post(`/api/auth/account_validations`, snakecaseKeys(formState))
      .then((res) => {
        let passwordErrors = res.data?.password
        if (!passwordErrors) {
          setPasswordErrors(null)
          setIsPasswordValid(true)
        } else {
          setIsPasswordValid(false)
          setPasswordErrors(passwordErrors)
        }
      })
      .catch(console.log)
  }

  const handleRequirementsClick = (e) => {
    setAnchorEl(e.currentTarget)
  }

  const _handleSubmit = (e) => {
    e.preventDefault()
    if (isLoading) return;
    actionHandler(e)
  }

  const renderRequirementsButton = () => (
    <ButtonBase
      onClick={handleRequirementsClick}
      style={{ position: 'absolute', top: 0, right: 0 }}>
      <RightLabelText>
        <PiInfo />
        &nbsp;requirements
      </RightLabelText>
    </ButtonBase>
  )

  const renderPasswordAcceptableText = () => (
    <span style={{ color: colors.success(500) }}>password acceptable</span>
  )

  const renderPasswordHelperText = () => {
    if (isPasswordValid && !formErrors?.password) {
      return renderPasswordAcceptableText()
    } else if (formErrors?.password?.[0]) {
      return formErrors?.password?.[0]
    } else {
      return ' '
    }
  }

  return (
    <FormContainer onSubmit={_handleSubmit}>
      <Popover
        slotProps={{ paper: { style: { backgroundColor: 'transparent', marginTop: size(-2) } }  }}
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}>
        <PasswordRequirementsAlert />
      </Popover>
      <PositionedTopProgress isLoadingDelayed={isLoadingDelayed} />
      <FlexSpacerDiv />
      <InputContainer>
        <FormTitle>
            Sign Up
        </FormTitle>
        <SheerFormTextField
          required
          name='email'
          label='Email'
          type='email'
          placeholder='Your email'
          value={formState?.email}
          onChange={handleChange}
          error={formErrors?.email}
          helperText={formErrors?.email?.[0]}
        />
        <div style={{ position: 'relative', width: '100%' }}>
          {renderRequirementsButton()}
          <SheerFormTextField
            required
            name='password'
            label='Password'
            type={isPasswordMasked ? 'password' : 'text'}
            placeholder='Your password'
            value={formState?.password}
            onChange={handleChange}
            error={formErrors?.password}
            helperText={renderPasswordHelperText()}
            slotProps={{ input: {
              endAdornment: (
                <EyeMaskEndAdornment
                  isMasked={isPasswordMasked}
                  onClick={(e) => setIsPasswordMasked(!isPasswordMasked)}
                />
              ),
            }}}
          />
        </div>
        <LinksContainer style={{ marginTop: size(2) }}>
          <FormLink
            to='?modal-login=true&remove_background=true'>
            Back to Log In
          </FormLink>
        </LinksContainer>
      </InputContainer>
      <FlexSpacerDiv />
      <ButtonGroupClearFix />
      <BottomButtonGroup>
        <ActionButton
          type='submit'
          variant='contained'>
          {isLoading
            ? 'Loading...'
            : <>Next&nbsp;<PiArrowRightBold /></>
          }
        </ActionButton>
      </BottomButtonGroup>
    </FormContainer>
  )
}

export default SignupModalEmailPass
